// VehicleTrendHighChart.tsx
import { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    Grid,
    IconButton,
    Stack,
    Button,
    Autocomplete,
    TextField,
} from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import CountUp from 'react-countup';
import axios from 'axios';
import COEResultsCard from './COEResultsCard';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import HomepageTopAd from './HomePageTopAd';
import axiosInstance from '../services/axiosInstance';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import fallbackImage from '../assets/icons/fallbackImage.png';


type AdSpaceItem = {
    adSpaceId: number;
    dealerId: number;
    dealerName: string;
    name: string;
    startDate: string;
    endDate: string;
    adSpaceThumbnailUrl: string;
    adSpacePositionId: number;
    adSpacePositionName: string;
    adPositionCode: string;
    width: number;
    height: number;
    adSpaceImageId: number;
    fileName: string;
    adSpaceImageUrl: string;
};

type AdSpaceBannerProps = {
    adSpaceList: AdSpaceItem[];
};

const HeroSection = () => {

    const [adSpaceList, setAdSpaceList] = useState<any[]>([]);
    const navigate = useNavigate();
    const [vehicles, setVehicles] = useState<any[]>([]);
    const [bodyTypesData, setvehicleBodyTypeData] = useState<any[]>([]);
    const [vehicleClassData, setVehicleClassData] = useState<any[]>([]);
    const [vehicleBrandData, setVehicleBrandData] = useState<any[]>([]);
    const [vehicleModelData, setVehicleModelData] = useState<any[]>([]);
    const [dealerData, setDealerData] = useState<any[]>([]);
    const [showAll, setShowAll] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [selectedVehicleClassId, setSelectedVehicleClassId] = useState<number | null>(null);
    const [lightCommercialList, setLightCommercialList] = useState<any[]>([]);
    const [heavyCommercialList, setHeavyCommercialList] = useState<any[]>([]);
    const [adSpaceBanner, setAdSpaceBanner] = useState<any[]>([]);

    const combinedOptions = [
        ...vehicleBrandData.map((b) => ({ type: 'Brand', label: b.description })),
        ...vehicleModelData.map((m) => ({ type: 'Model', label: m.description })),
    ];


    useEffect(() => {

        const fetchAdData = async () => {
            try {
                const response = await axiosInstance.post(
                    "/CustomerVehicle/VehicleMultipleDataList",
                    { adSpaceData: true },
                    { headers: { "Content-Type": "application/json" } }
                );

                const result = response.data as {
                    responseCode: string;
                    responseMessage?: string;
                    responseData?: { adSpaceData?: any[] };
                };

                if (result.responseCode === "00" && result.responseData) {
                    setAdSpaceList(result.responseData.adSpaceData || []);
                } else {
                    enqueueSnackbar(result.responseMessage || "⚠️ Failed to load ad data", {
                        variant: "error",
                    });
                }
            } catch {
                enqueueSnackbar("❌ Error fetching ad data", { variant: "error" });
            }
        };

        fetchAdData();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.post(
                    "/CustomerVehicle/VehicleMultipleDataList",
                    {
                        vehicleBodyTypeData: true,
                        vehicleTransmissionTypeData: true,
                        vehicleClassData: true,
                        vehicleBrandData: true,
                        vehicleModelData: true,
                        dealerCompanyData: true,
                        adSpaceData: true,
                    },
                    { headers: { "Content-Type": "application/json" } }
                );

                const result = response.data as {
                    responseCode: string;
                    responseMessage?: string;
                    responseData?: {
                        vehicleBodyTypeData?: any[];
                        vehicleTransmissionTypeData?: any[];
                        vehicleClassData?: any[];
                        vehicleBrandData?: any[];
                        vehicleModelData?: any[];
                        dealerCompanyData?: any[];
                        adSpaceData?: any[];
                    };
                };

                if (result.responseCode === "00" && result.responseData) {
                    setvehicleBodyTypeData(result.responseData.vehicleBodyTypeData || []);
                    setVehicleClassData(result.responseData.vehicleClassData || []);
                    setVehicleBrandData(result.responseData.vehicleBrandData || []);
                    setVehicleModelData(result.responseData.vehicleModelData || []);
                    setDealerData(result.responseData.dealerCompanyData || []);
                    setAdSpaceList(result.responseData.adSpaceData || []);

                    // 🔹 filter only homepage banner(s)
                    const homePageBanner = (result.responseData.adSpaceData || []).filter(
                        (ad: any) => ad.adPositionCode === "homePage-Banner"
                    );
                    setAdSpaceBanner(homePageBanner);
                } else {
                    enqueueSnackbar(result.responseMessage || "⚠️ Failed to load data", {
                        variant: "error",
                    });
                }
            } catch {
                enqueueSnackbar("❌ Error fetching vehicle metadata", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);


    const FilterChip = ({ id, imageUrl, description, filterType }: any) => {
        const displayImage = imageUrl || fallbackImage;

        return (
            <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                spacing={0.3}
                onClick={() => {
                    if (id !== undefined) {
                        navigate(`/vehicle-listing?${filterType}=${encodeURIComponent(id.toString())}`);
                    }
                }}
                sx={{
                    cursor: 'pointer',
                    height: 120,
                    width: 110,
                }}
            >
                <Box
                    component="img"
                    src={displayImage}
                    alt={description}
                    loading="lazy" // ✅ this is the lazy load!
                    sx={{ width: 96, height: 96, objectFit: 'contain' }}
                />
                <Typography
                    variant="caption"
                    fontSize={10}
                    textAlign="center"
                    sx={{
                        wordBreak: 'break-word',
                        lineHeight: 1.1,
                    }}
                >
                    {description}
                </Typography>
            </Stack>
        );
    };

    const classColorMap: Record<string, { bg: string; text: string }> = {
        'Heavy Commercial': { bg: '#1e3a8a', text: '#fff' },
        'Light Commercial': { bg: '#c7d2fe', text: '#111827' },
        'Private': { bg: '#e0e7ff', text: '#111827' },
    };

    const visibleClasses = vehicleClassData.filter(
        (cls: any) => cls.description !== "Private"
    );

    const filteredBodyTypes = bodyTypesData.filter(
        (type) => selectedVehicleClassId === null || type.vehicleClassId === selectedVehicleClassId
    );



    // Assuming this block is inside the render/return of your parent component

    return (
        // FIX 1: Corrected the outer Grid container syntax
        <Grid container spacing={1}>


            {/* Vehicle Class Buttons (Centered) */}
            <Grid size={{ xs: 12 }}>
                <Grid
                    container
                    spacing={2}
                    sx={{ justifyContent: 'center', textAlign: 'center' }}
                >
                    {visibleClasses.map((cls) => {
                        const isSelected = selectedVehicleClassId === cls.vehicleClassId;
                        return (
                            <Grid size={{ xs: 6, sm: 4, md: 'auto' }} key={cls.vehicleClassId}>
                                <Button
                                    fullWidth
                                    onClick={() => setSelectedVehicleClassId(cls.vehicleClassId)}
                                    sx={{
                                        fontWeight: 700,
                                        borderRadius: 2,
                                        border: '1px solid #ccc',
                                        color: isSelected ? '#fff' : '#000',
                                        backgroundColor: isSelected ? '#FFA100' : '#f5f5f5',
                                        boxShadow: isSelected ? '0px 4px 8px rgba(255, 161, 0, 0.4)' : 'none',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            backgroundColor: '#FFB733',
                                            color: '#fff',
                                            boxShadow: '0px 6px 12px rgba(255, 177, 51, 0.4)',
                                        },
                                    }}
                                >
                                    {cls.description.toUpperCase()}
                                </Button>
                            </Grid>
                        );
                    })}

                    {/* All Class Button (Centered) */}
                    <Grid size={{ xs: 6, sm: 4, md: 'auto' }}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => setSelectedVehicleClassId(null)}
                            sx={{
                                fontWeight: 700,
                                borderRadius: 2,
                                backgroundColor: '#0f172a',
                                color: '#fff',
                                boxShadow: '0px 4px 8px rgba(15, 23, 42, 0.3)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    backgroundColor: '#FFA100',
                                    color: '#000',
                                    boxShadow: '0px 6px 12px rgba(255, 161, 0, 0.4)',
                                },
                            }}
                        >
                            ALL CLASS
                        </Button>
                    </Grid>
                </Grid>
            </Grid>

            {/* Search Bar */}
            {/* Reduced size for sm and md to make it thinner, e.g., 6/12 for small and 4/12 for medium screens. */}
            {/* The grid system automatically centers the item if it's the only one and not full width (12) by default, but we'll use auto margins for safety. */}
            <Grid
                size={{ xs: 12, sm: 8, md: 6 }}
                // Add margin auto to center the item within its parent Grid container
                sx={{
                    margin: '0 auto',
                    display: 'flex',
                    justifyContent: 'center'
                }}
            >
                {/* Removed the extra Box with justifyContent: 'center' as the Grid/Grid item handles centering now */}
                <Box sx={{ width: '100%' }}>
                    <Autocomplete
                        fullWidth
                        size="medium"
                        freeSolo
                        options={searchText.length > 0 ? combinedOptions : []}
                        getOptionLabel={(option) =>
                            typeof option === 'string' ? option : option.label
                        }
                        filterOptions={(options, { inputValue }) =>
                            options.filter((option) =>
                                option.label.toLowerCase().includes(inputValue.toLowerCase())
                            )
                        }
                        openOnFocus={false}
                        onChange={(event, value) => {
                            const query = encodeURIComponent(
                                typeof value === 'string' ? value : value?.label || ''
                            );
                            if (query) {
                                navigate(`/vehicle-listing?searchKeyword=${query}`);
                            }
                        }}
                        inputValue={searchText}
                        onInputChange={(event, newInputValue) => setSearchText(newInputValue)}
                        renderOption={(props, option) => (
                            <li {...props}>
                                {option.label}
                                <Typography variant="caption" sx={{ ml: 1, color: 'gray' }}>
                                    ({option.type})
                                </Typography>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Search cars by Brand or Model"
                                variant="outlined"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        const query = encodeURIComponent(searchText.trim());
                                        if (query) {
                                            navigate(`/vehicle-listing?searchKeyword=${query}`);
                                        }
                                    }
                                }}
                                sx={{
                                    '.MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        backgroundColor: '#f9f9f9',
                                        boxShadow: '0px 2px 6px rgba(0,0,0,0.1)',
                                        '&:hover': {
                                            boxShadow: '0px 4px 12px rgba(0,0,0,0.15)',
                                        },
                                        '&.Mui-focused': {
                                            boxShadow: '0px 0px 0px 2px #FFA100',
                                        },
                                    },
                                }}
                            />
                        )}
                    />
                </Box>
            </Grid>



            {/* ===below got other things */}

            <Grid size={{ xs: 12, sm: 6, md: 8 }}>
                {!loading && (
                    <>



                        {/* Body Type Icons */}
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold" color="#1e293b">
                            Choose by Body Type
                        </Typography>

                        <Stack direction="row" flexWrap="wrap" justifyContent="flex-start" gap={1}>
                            {(showAll ? filteredBodyTypes : filteredBodyTypes.slice(0, 20)).map(
                                (type, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            // FIX 6: Use responsive width to enforce stable rows on mobile.
                                            // 20% allows 5 icons per row (if parent width allows).
                                            width: { xs: '20%', sm: 'auto' },
                                            minWidth: { xs: 60, sm: 'auto' }, // Ensure icons are never too small
                                            display: 'flex',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <FilterChip
                                            id={type.vehicleBodyTypeId}
                                            imageUrl={type.imageUrl}
                                            description={type.description}
                                            filterType="vehicleBodyTypeIds"
                                        />
                                    </Box>
                                )
                            )}
                        </Stack>

                        {/* View More/Less Button */}
                        {filteredBodyTypes.length > 14 && (
                            <Box sx={{ textAlign: 'right', mt: 1 }}>
                                <Button
                                    size="small"
                                    onClick={() => setShowAll((prev) => !prev)}
                                    sx={{ textTransform: 'none' }}
                                >
                                    {showAll ? 'View Less' : 'View More'}
                                </Button>
                            </Box>
                        )}
                    </>

                )}
            </Grid>

            {/* Ad Section (Right Side) */}
            {/* FIX 2: Corrected Grid item syntax (size -> item xs/md) */}
            <Grid size={{ xs: 12, md: 4 }} display="flex" justifyContent="center" alignItems="center">
                <Box sx={{
                    width: { xs: 380, md: '100%' }, // Set fixed width on small screens to prevent shrinking
                    mx: 'auto' // Center the box on small screens
                }}>
                    <HomepageTopAd adSpaceList={adSpaceList} />
                </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 12 }}>
                {/* COE Box - Left */}
                <COEResultsCard />
            </Grid>


        </Grid>
    );

};

export default HeroSection;
